import {
    Add,
    CheckBoxOutlineBlank,
    CheckBoxOutlined,
    Delete,
    HttpsOutlined,
} from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    darken,
    Divider,
    FormControlLabel,
    IconButton,
    lighten,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";

import SchemaBuilder from "../schemaBuilder";
import { contarSlotsDoDraft } from "../../../utils/dashboardSchemaDraft";
import stylesFn from "../styles";
import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxOutlined fontSize="small" />;

/**
 * Formulário de Predefinições + Schema do Modelo selecionado.
 *
 * Componente "burro": recebe todo o estado por props.
 * Reutilizado tanto no painel direito (desktop) quanto dentro
 * do Drawer (mobile), garantindo paridade visual com clientesSwap.
 */
export default function TemplateForm({
    nomeModelo,
    fabricanteNome,
    funcaoNome,
    editar,
    salvando,
    activeTab,
    setActiveTab,
    onEdit,
    onSave,
    onCancel,

    // dados do formulário
    groupHosts,
    setGroupHosts,
    templates,
    setTemplates,
    templatesList,
    macros,
    handleMacroChange,
    addMacro,
    removeMacro,

    // configurações SSH/Telnet
    ssh,
    setSsh,
    userSSH,
    setUserSSH,
    portaSSH,
    setPortaSSH,
    senhaSSH,
    setSenhaSSH,
    telnet,
    setTelnet,
    userTelnet,
    setUserTelnet,
    portaTelnet,
    setPortaTelnet,
    senhaTelnet,
    setSenhaTelnet,

    // schema (dashboard_schema do Modelo)
    schemaDraft,
    setSchemaDraft,
    dashboardSchema,
    schemaAvancado,
    setSchemaAvancado,
    schemaAvancadoTexto,
    setSchemaAvancadoTexto,
    schemaAvancadoErro,
    setSchemaAvancadoErro,
    sugestoesItensSchema,
    onAbrirBulkSchema,
    notify,

    // documentação (markdown)
    documentacao,
    setDocumentacao,

    // visual
    showHeader = true,
}) {
    const theme = useTheme();
    const styles = useMemo(() => stylesFn(theme), [theme]);

    const TabCustom = ({ index, labelText, ...props }) => (
        <Tab
            label={<Box sx={styles.tabLabel}>{labelText}</Box>}
            sx={{
                ...styles.tab,
                ...(activeTab === index && {
                    borderColor: "divider",
                    backgroundColor: theme.palette.background.paper,
                }),
                ...(activeTab !== index && {
                    backgroundColor:
                        theme.palette.mode === "dark"
                            ? darken(theme.palette.background.paper, 0.05)
                            : lighten(theme.palette.secondary.main, 0.95),
                }),
                "&:hover": {
                    borderColor:
                        activeTab === index
                            ? "divider"
                            : darken(theme.palette.background.paper, 0.5),
                    backgroundColor:
                        activeTab === index
                            ? "background.paper"
                            : lighten(theme.palette.background.paper, 0.05),
                },
            }}
            {...props}
        />
    );

    const renderPredefinicoesTab = () => (
        <Stack direction="column" gap="10px" width="100%">
            {/* Grupos de hosts */}
            <Box width="100%">
                <Typography color="primary" fontWeight={500} mt="5px">
                    Grupo de hosts
                </Typography>
                {!editar && <Divider sx={{ mt: "5px" }} />}
            </Box>

            {!editar && (
                <Box component="ul" sx={styles.chipList}>
                    {groupHosts.length === 0 && (
                        <li key="semGrupos">
                            <Chip
                                sx={{ bgcolor: theme.palette.background.arquive }}
                                label="Sem Grupos"
                            />
                        </li>
                    )}
                    {groupHosts.map((grupo, index) => (
                        <li key={grupo.groupid || index}>
                            <Chip
                                sx={{
                                    bgcolor: theme.palette.chip.waitingReturn,
                                    color: "white",
                                }}
                                label={grupo.name}
                            />
                        </li>
                    ))}
                </Box>
            )}

            {editar && (
                <Autocomplete
                    multiple
                    freeSolo
                    sx={{ width: "100%" }}
                    id="grupo-hosts-autocomplete"
                    isOptionEqualToValue={(option, value) =>
                        option.groupid === value.groupid
                    }
                    disableCloseOnSelect
                    value={groupHosts}
                    options={[]}
                    onChange={(_event, newValue) => {
                        const normalized = newValue.map((item, index) =>
                            typeof item === "string"
                                ? { groupid: `tmp-${index}`, name: item }
                                : item
                        );
                        setGroupHosts(normalized);
                    }}
                    getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.name
                    }
                    renderOption={(props, option, { selected: sel }) => (
                        <li {...props} key={option.groupid}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={sel}
                            />
                            {option.name}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label="Grupos" variant="standard" />
                    )}
                />
            )}

            {/* Templates */}
            <Box width="100%">
                <Typography color="primary" fontWeight={500} mt="15px">
                    Templates
                </Typography>
                {!editar && <Divider sx={{ mt: "5px" }} />}
            </Box>

            {!editar && (
                <Box component="ul" sx={styles.chipList}>
                    {templates.length === 0 && (
                        <li key="semTemplates">
                            <Chip
                                sx={{ bgcolor: theme.palette.background.arquive }}
                                label="Sem Templates"
                            />
                        </li>
                    )}
                    {templates.map((template, index) => (
                        <li key={template.templateid || index}>
                            <Chip
                                sx={{
                                    bgcolor: theme.palette.chip.waitingReturn,
                                    color: "white",
                                }}
                                label={template.name}
                            />
                        </li>
                    ))}
                </Box>
            )}

            {editar && (
                <Autocomplete
                    multiple
                    sx={{ width: "100%" }}
                    id="templates-autocomplete"
                    isOptionEqualToValue={(option, value) =>
                        option.templateid === value.templateid
                    }
                    disableCloseOnSelect
                    value={templates}
                    options={templatesList}
                    onChange={(_event, newValue) => setTemplates(newValue)}
                    getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.name
                    }
                    renderOption={(props, option, { selected: sel }) => (
                        <li {...props} key={option.templateid}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={sel}
                            />
                            {option.name}
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField {...params} label="Templates" variant="standard" />
                    )}
                />
            )}

            {/* Macros */}
            <Stack direction="row" alignItems="center">
                <Box width="100%">
                    <Typography color="primary" fontWeight={500} mt="15px">
                        Macros
                    </Typography>
                    {!editar && <Divider sx={{ mt: "5px" }} />}
                </Box>
                {editar && (
                    <IconButton onClick={addMacro}>
                        <Add color="primary" />
                    </IconButton>
                )}
            </Stack>

            {!editar && (
                <Stack
                    direction="column"
                    justifyContent="space-around"
                    minWidth="400px"
                    gap="10px"
                >
                    <Stack direction="row" key="title" gap="10px">
                        <Typography sx={[styles.textTitle, { width: "220px" }]}>Macro</Typography>
                        <Typography sx={[styles.textTitle, { width: "220px" }]}>Valor</Typography>
                        <Typography sx={[styles.textTitle, { width: "220px" }]}>Tag</Typography>
                    </Stack>
                    {macros.map((macro, index) => (
                        <Stack direction="row" key={index} gap="10px">
                            <Box sx={styles.macroCampo}>{macro.macro}</Box>
                            <Box sx={styles.macroCampo}>{macro.valor}</Box>
                            <Box sx={styles.macroCampo}>{macro.tag}</Box>
                        </Stack>
                    ))}
                </Stack>
            )}

            {editar &&
                macros.map((macro, index) => (
                    <Stack
                        key={index}
                        direction="row"
                        justifyContent="start"
                        alignItems="center"
                        gap={2}
                    >
                        <TextField
                            name="macro"
                            variant="standard"
                            label="Macro"
                            sx={styles.textfield}
                            value={macro.macro}
                            onChange={(event) => handleMacroChange(index, event)}
                        />
                        <TextField
                            name="valor"
                            variant="standard"
                            label="Valor"
                            value={macro.valor}
                            sx={styles.textfield}
                            onChange={(event) => handleMacroChange(index, event)}
                        />
                        <TextField
                            name="tag"
                            variant="standard"
                            label="Tag"
                            value={macro.tag || ""}
                            sx={styles.textfield}
                            onChange={(event) => handleMacroChange(index, event)}
                        />
                        {macros.length > 1 && (
                            <IconButton onClick={() => removeMacro(index)} sx={{ mt: 3 }}>
                                <Delete color="secondary" />
                            </IconButton>
                        )}
                    </Stack>
                ))}

            {/* Configurações */}
            <Box width="100%">
                <Typography color="primary" fontWeight={500} mt="15px">
                    Configurações do modelo
                </Typography>
                {!editar && <Divider sx={{ mt: "5px" }} />}
            </Box>

            {editar && (
                <>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={ssh}
                                onChange={() => setSsh(!ssh)}
                            />
                        }
                        label="SSH"
                    />
                    {ssh && (
                        <Stack direction="row" gap={2} mt="-10px" mb="10px" flexWrap="wrap">
                            <TextField
                                disabled={!ssh}
                                name="userSSH"
                                variant="standard"
                                label="User SSH"
                                value={userSSH}
                                sx={styles.textfield}
                                onChange={(event) => setUserSSH(event.target.value)}
                            />
                            <TextField
                                disabled={!ssh}
                                name="portaSSH"
                                variant="standard"
                                type="number"
                                label="Porta SSH"
                                value={portaSSH}
                                sx={styles.textfield}
                                onChange={(event) => setPortaSSH(event.target.value)}
                            />
                            <TextField
                                disabled={!ssh}
                                name="senhaSSH"
                                variant="standard"
                                label="Senha SSH"
                                value={senhaSSH}
                                sx={styles.textfield}
                                onChange={(event) => setSenhaSSH(event.target.value)}
                            />
                        </Stack>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={telnet}
                                onChange={() => setTelnet(!telnet)}
                            />
                        }
                        label={<Typography fontSize="16px">Telnet</Typography>}
                    />
                    {telnet && (
                        <Stack direction="row" gap={2} mt="-10px" mb="10px" flexWrap="wrap">
                            <TextField
                                name="userTelnet"
                                variant="standard"
                                label="User Telnet"
                                value={userTelnet}
                                sx={styles.textfield}
                                onChange={(event) => setUserTelnet(event.target.value)}
                            />
                            <TextField
                                name="portaTelnet"
                                variant="standard"
                                label="Porta Telnet"
                                value={portaTelnet}
                                sx={styles.textfield}
                                onChange={(event) => setPortaTelnet(event.target.value)}
                            />
                            <TextField
                                name="senhaTelnet"
                                variant="standard"
                                label="Senha Telnet"
                                value={senhaTelnet}
                                sx={styles.textfield}
                                onChange={(event) => setSenhaTelnet(event.target.value)}
                            />
                        </Stack>
                    )}
                </>
            )}

            {!editar && (
                <>
                    {!telnet && !ssh && (
                        <Chip
                            sx={{
                                bgcolor: theme.palette.background.arquive,
                                width: "150px",
                            }}
                            label="Sem Configurações"
                        />
                    )}
                    {telnet && (
                        <Stack direction="row" gap={2} flexWrap="wrap">
                            <Stack direction="column" alignItems="start" width="220px">
                                <Typography sx={styles.textTitle}>User Telnet</Typography>
                                <Typography sx={styles.textData}>{userTelnet}</Typography>
                            </Stack>
                            <Stack direction="column" alignItems="start" width="220px">
                                <Typography sx={styles.textTitle}>Porta Telnet</Typography>
                                <Typography sx={styles.textData}>{portaTelnet}</Typography>
                            </Stack>
                            <Stack direction="column" alignItems="start" width="220px">
                                <Typography sx={styles.textTitle}>Senha Telnet</Typography>
                                <Typography sx={styles.textData}>{senhaTelnet}</Typography>
                            </Stack>
                        </Stack>
                    )}
                    {ssh && (
                        <Stack direction="row" gap={2} mt="10px" flexWrap="wrap">
                            <Stack direction="column" alignItems="start" width="220px">
                                <Typography sx={styles.textTitle}>User SSH</Typography>
                                <Typography sx={styles.textData}>{userSSH}</Typography>
                            </Stack>
                            <Stack direction="column" alignItems="start" width="220px">
                                <Typography sx={styles.textTitle}>Porta SSH</Typography>
                                <Typography sx={styles.textData}>{portaSSH}</Typography>
                            </Stack>
                            <Stack direction="column" alignItems="start" width="220px">
                                <Typography sx={styles.textTitle}>Senha SSH</Typography>
                                <Typography sx={styles.textData}>{senhaSSH}</Typography>
                            </Stack>
                        </Stack>
                    )}
                </>
            )}
        </Stack>
    );

    const renderSchemaTab = () => (
        <Box width="100%">
            <SchemaBuilder
                draft={schemaDraft}
                setDraft={setSchemaDraft}
                editar={editar}
                dashboardSchema={dashboardSchema}
                avancado={schemaAvancado}
                setAvancado={setSchemaAvancado}
                avancadoTexto={schemaAvancadoTexto}
                setAvancadoTexto={setSchemaAvancadoTexto}
                avancadoErro={schemaAvancadoErro}
                setAvancadoErro={setSchemaAvancadoErro}
                sugestoesItens={sugestoesItensSchema}
                notify={notify}
                onAbrirBulk={onAbrirBulkSchema}
                podeAplicarEmMassa={contarSlotsDoDraft(schemaDraft) > 0}
                styles={styles}
            />
        </Box>
    );

    const markdownPreviewSx = {
        "& h1, & h2, & h3, & h4, & h5, & h6": {
            color: theme.palette.primary.main,
            mt: 2,
            mb: 1,
        },
        "& p": { my: 1, lineHeight: 1.6 },
        "& ul, & ol": { pl: 3, my: 1 },
        "& code": {
            bgcolor: theme.palette.background.arquive,
            px: 0.6,
            py: 0.2,
            borderRadius: 0.5,
            fontSize: "0.9em",
            fontFamily: "monospace",
        },
        "& pre": {
            bgcolor: theme.palette.background.arquive,
            p: 1.5,
            borderRadius: 1,
            overflowX: "auto",
        },
        "& pre code": { bgcolor: "transparent", p: 0 },
        "& blockquote": {
            borderLeft: `4px solid ${theme.palette.divider}`,
            pl: 2,
            color: theme.palette.text.secondary,
            my: 1,
        },
        "& table": {
            borderCollapse: "collapse",
            my: 1,
            "& th, & td": {
                border: `1px solid ${theme.palette.divider}`,
                px: 1,
                py: 0.5,
            },
            "& th": {
                bgcolor: theme.palette.background.arquive,
            },
        },
        "& a": { color: theme.palette.primary.main },
        "& img": { maxWidth: "100%" },
        "& hr": {
            border: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
            my: 2,
        },
    };

    const renderDocumentacaoTab = () => {
        if (!editar) {
            if (!documentacao || !documentacao.trim()) {
                return (
                    <Box sx={styles.emptyStateBox} minHeight="200px">
                        <Chip
                            sx={{
                                bgcolor: theme.palette.background.arquive,
                                width: "180px",
                            }}
                            label="Sem Documentação"
                        />
                    </Box>
                );
            }
            return (
                <Box sx={{ width: "100%", ...markdownPreviewSx }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {documentacao}
                    </ReactMarkdown>
                </Box>
            );
        }

        return (
            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={2}
                width="100%"
                alignItems="stretch"
            >
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Typography
                        color="primary"
                        fontWeight={500}
                        fontSize={14}
                        mb={0.5}
                    >
                        Markdown
                    </Typography>
                    <TextField
                        multiline
                        minRows={18}
                        maxRows={28}
                        fullWidth
                        variant="outlined"
                        placeholder="# Título&#10;&#10;Escreva aqui a documentação do modelo em **Markdown**..."
                        value={documentacao || ""}
                        onChange={(e) => setDocumentacao(e.target.value)}
                        InputProps={{
                            sx: {
                                fontFamily: "monospace",
                                fontSize: "0.9rem",
                                alignItems: "flex-start",
                            },
                        }}
                    />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Typography
                        color="primary"
                        fontWeight={500}
                        fontSize={14}
                        mb={0.5}
                    >
                        Preview
                    </Typography>
                    <Paper
                        variant="outlined"
                        sx={{
                            flex: 1,
                            p: 2,
                            minHeight: 300,
                            maxHeight: 560,
                            overflowY: "auto",
                            ...markdownPreviewSx,
                        }}
                    >
                        {documentacao && documentacao.trim() ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {documentacao}
                            </ReactMarkdown>
                        ) : (
                            <Typography color="text.disabled" fontStyle="italic">
                                O preview aparecerá aqui conforme você escreve.
                            </Typography>
                        )}
                    </Paper>
                </Box>
            </Stack>
        );
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {showHeader && (
                <Stack direction="row" sx={styles.headerForm}>
                    <Box sx={{ minWidth: 0 }}>
                        {(fabricanteNome || funcaoNome) && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                            >
                                {[fabricanteNome, funcaoNome, "Modelo"]
                                    .filter(Boolean)
                                    .join(" › ")}
                            </Typography>
                        )}
                        {!(fabricanteNome || funcaoNome) && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                            >
                                Modelo
                            </Typography>
                        )}
                        <Typography variant="h5" fontWeight={600} color="primary" noWrap>
                            {nomeModelo}
                        </Typography>
                    </Box>

                    <Stack direction="row" alignItems="center" spacing={1}>
                        {editar ? (
                            <>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={salvando}
                                    onClick={onCancel}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    disabled={salvando}
                                    onClick={onSave}
                                    startIcon={
                                        salvando ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : null
                                    }
                                >
                                    {salvando ? "Salvando..." : "Salvar"}
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<HttpsOutlined />}
                                onClick={onEdit}
                            >
                                Editar
                            </Button>
                        )}
                    </Stack>
                </Stack>
            )}

            {!showHeader && editar && (
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={1}
                    sx={{ pb: 1 }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={salvando}
                        onClick={onCancel}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={salvando}
                        onClick={onSave}
                        startIcon={
                            salvando ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : null
                        }
                    >
                        {salvando ? "Salvando..." : "Salvar"}
                    </Button>
                </Stack>
            )}

            {!showHeader && !editar && (
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    sx={{ pb: 1 }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<HttpsOutlined />}
                        onClick={onEdit}
                    >
                        Editar
                    </Button>
                </Stack>
            )}

            <Box sx={styles.containerTabsNActionButtons}>
                <Tabs
                    value={activeTab}
                    onChange={(_e, v) => setActiveTab(v)}
                    sx={styles.tabs}
                    variant="scrollable"
                    scrollButtons={false}
                >
                    <TabCustom index={0} labelText="Predefinições" value={0} />
                    <TabCustom index={1} labelText="Schema" value={1} />
                    <TabCustom index={2} labelText="Documentação" value={2} />
                </Tabs>
            </Box>

            <Paper
                variant="outlined"
                sx={{
                    display: "block",
                    p: 2,
                    borderRadius: 1,
                    mt: 0,
                    flex: 1,
                    overflowY: "auto",
                }}
            >
                {activeTab === 0 && renderPredefinicoesTab()}
                {activeTab === 1 && renderSchemaTab()}
                {activeTab === 2 && renderDocumentacaoTab()}
            </Paper>
        </Box>
    );
}
